import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Loader2, X as CloseIcon, AlertTriangle, ArrowLeft, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import yLogoDark from "@/assets/brand/y-logo-dark.png";
import { AppleLogo, GoogleLogo } from "@/components/BrandLogos";
import { useGoogleSignIn } from "@/lib/googleAuth";
import { useAppleSignIn } from "@/lib/appleAuth";
import { useAuth } from "@/context/AuthContext";
import { apiError as fmtApiError } from "@/lib/api";
import { Seo } from "@/lib/useSeo";

/**
 * HelpDeleteAuth — Delete-flow auth options page.
 *
 * Same three "Continue with …" buttons as the Landing page — Apple, Google,
 * Email — but themed as the entry into the destructive Delete-Account flow
 * (red accents, prominent warning, "Sign in to continue deletion").
 *
 * On successful auth we redirect to `/help/delete-account` instead of the
 * usual /home path.  That override is honoured by AuthContext through the
 * `brontoy_post_login_redirect` sessionStorage key, which we set before
 * kicking off any auth flow (SSO or email).
 */
export default function HelpDeleteAuth() {
  const navigate = useNavigate();
  const { googleWebLogin, appleWebLogin, authed } = useAuth();
  const [emailChoice, setEmailChoice] = useState(false);
  const [ssoBusy, setSsoBusy] = useState(null); // "apple" | "google" | null
  const [authError, setAuthError] = useState(null);

  // Persist the "return-to" for AuthContext to read after login completes.
  useEffect(() => {
    try {
      sessionStorage.setItem("brontoy_post_login_redirect", "/help/delete-account");
    } catch {
      /* storage disabled – silently ignored */
    }
    return () => {
      // NOTE: do NOT clear on unmount — the /login and /signup pages depend
      // on the flag surviving navigation.  It's cleared by AuthContext once
      // consumed, or when the user returns to `/help` explicitly.
    };
  }, []);

  // If the user is ALREADY signed in, skip the auth wall.
  useEffect(() => {
    if (authed) navigate("/help/delete-account", { replace: true });
  }, [authed, navigate]);

  const surfaceError = (provider, message) => {
    setAuthError({ provider, message });
    toast.error(message);
  };

  const { ready: googleReady, error: googleError, signIn: googleSignIn, renderRef } =
    useGoogleSignIn({
      onCredential: async (idToken) => {
        try {
          setSsoBusy("google");
          setAuthError(null);
          await googleWebLogin(idToken);
        } catch (err) {
          surfaceError("google", fmtApiError(err) || "Google sign-in failed. Please try again.");
        } finally {
          setSsoBusy(null);
        }
      },
      onError: (err) => surfaceError("google", err?.message || "Google Sign-In failed to load."),
    });

  const { ready: appleReady, error: appleError, signIn: appleSignIn } = useAppleSignIn();

  const goSocial = async (kind) => {
    if (kind === "google") {
      if (!googleReady) {
        toast.info("Google Sign-In is loading. Please try again in a moment.");
        return;
      }
      if (googleError) {
        surfaceError("google", "Google Sign-In failed to load. Check your connection.");
        return;
      }
      setSsoBusy("google");
      setAuthError(null);
      googleSignIn();
      setTimeout(() => setSsoBusy((s) => (s === "google" ? null : s)), 1200);
      return;
    }
    if (kind === "apple") {
      if (!appleReady) {
        toast.info("Sign in with Apple is loading. Please try again in a moment.");
        return;
      }
      if (appleError) {
        surfaceError("apple", "Apple Sign-In failed to load. Check your connection.");
        return;
      }
      try {
        setSsoBusy("apple");
        setAuthError(null);
        const { code, identityToken } = await appleSignIn();
        await appleWebLogin(code, identityToken);
      } catch (err) {
        const ac = err?.appleErrorCode;
        if (ac === "cancelled" || ac === "in_flight") {
          /* silent */
        } else if (ac) {
          surfaceError("apple", err.message);
        } else {
          surfaceError("apple", fmtApiError(err) || "Sign in with Apple didn't complete. Please try again.");
        }
      } finally {
        setSsoBusy(null);
      }
    }
  };

  return (
    <div
      className="min-h-dvh w-full bg-black text-white flex flex-col relative overflow-hidden"
      data-testid="help-delete-auth-screen"
      style={{
        fontFamily:
          "'Plus Jakarta Sans','TwitterChirp','Segoe UI',Helvetica,Arial,sans-serif",
      }}
    >
      <Seo
        title="Delete Account — BrontoY"
        description="Sign in to permanently delete your BrontoY account and all associated data."
        canonicalPath="/help/delete"
      />

      {/* Red-tinged ambient glow — signals destructive flow */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div
          style={{
            position: "absolute",
            top: "-20%",
            left: "-10%",
            width: "700px",
            height: "700px",
            background:
              "radial-gradient(closest-side, rgba(244,63,94,0.28), transparent)",
            filter: "blur(50px)",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-25%",
            right: "-15%",
            width: "650px",
            height: "650px",
            background:
              "radial-gradient(closest-side, rgba(190,18,60,0.22), transparent)",
            filter: "blur(50px)",
          }}
        />
      </div>

      <header className="relative z-10 h-14 px-4 md:px-8 flex items-center justify-between border-b border-white/10">
        <button
          type="button"
          onClick={() => navigate("/help")}
          className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-[13.5px] font-medium"
          data-testid="help-delete-auth-back"
        >
          <ArrowLeft size={16} strokeWidth={2.4} /> Back
        </button>
        <img
          src={yLogoDark}
          alt="BrontoY"
          className="h-7 w-auto"
          draggable={false}
        />
        <span className="w-10" />
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-[440px] flex flex-col">
          {/* Red warning badge */}
          <div className="flex items-center justify-center mb-6">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, rgba(244,63,94,0.20), rgba(190,18,60,0.10))",
                border: "1px solid rgba(244,63,94,0.35)",
              }}
            >
              <ShieldAlert size={28} className="text-red-400" strokeWidth={2.2} />
            </div>
          </div>

          <h1
            className="text-[30px] md:text-[38px] font-extrabold tracking-tight text-center leading-[1.1]"
            data-testid="help-delete-auth-title"
          >
            Delete Your Account
          </h1>
          <p className="mt-3 text-white/60 text-[14px] md:text-[15px] text-center max-w-[400px] mx-auto leading-relaxed">
            Sign in to confirm it&apos;s really you. Deletion is permanent and cannot be undone.
          </p>

          {/* Auth buttons */}
          <div className="mt-8 flex flex-col gap-3">
            {authError && (
              <div
                role="alert"
                className="flex items-start gap-3 rounded-2xl border border-red-500/40 bg-red-500/10 text-red-100 px-4 py-3"
                data-testid="help-delete-auth-error"
              >
                <AlertTriangle size={18} className="mt-0.5 shrink-0 text-red-300" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-bold text-red-200 mb-0.5">
                    {authError.provider === "apple"
                      ? "Sign in with Apple failed"
                      : authError.provider === "google"
                      ? "Sign in with Google failed"
                      : "Sign in failed"}
                  </div>
                  <div className="text-[12.5px] leading-[17px] break-words">
                    {authError.message}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setAuthError(null)}
                  className="shrink-0 rounded-full p-1 text-red-200 hover:bg-red-500/20"
                  aria-label="Dismiss error"
                >
                  <CloseIcon size={14} />
                </button>
              </div>
            )}

            {!emailChoice && (
              <>
                <SsoButton
                  onClick={() => goSocial("apple")}
                  disabled={ssoBusy === "apple"}
                  testId="help-delete-continue-apple"
                  Icon={AppleLogo}
                  label="Continue with Apple"
                />
                <SsoButton
                  onClick={() => goSocial("google")}
                  disabled={ssoBusy === "google"}
                  testId="help-delete-continue-google"
                  Icon={GoogleLogo}
                  label="Continue with Google"
                />
                <SsoButton
                  onClick={() => setEmailChoice(true)}
                  testId="help-delete-continue-email"
                  Icon={MailIcon}
                  label="Continue with Email"
                />
              </>
            )}

            {emailChoice && (
              <>
                <SsoButton
                  onClick={() => navigate("/login")}
                  testId="help-delete-choice-login"
                  label="Log In"
                />
                <SsoButton
                  onClick={() => navigate("/signup")}
                  testId="help-delete-choice-signup"
                  label="Sign Up"
                />
                <button
                  type="button"
                  onClick={() => setEmailChoice(false)}
                  className="text-white/60 hover:text-white text-[13px] font-medium mt-1 self-center"
                  data-testid="help-delete-choice-back"
                >
                  ← Back
                </button>
              </>
            )}
          </div>

          <p className="mt-6 text-white/45 text-[12px] text-center leading-[17px] max-w-[360px] mx-auto">
            Changed your mind?{" "}
            <button
              type="button"
              onClick={() => {
                try {
                  sessionStorage.removeItem("brontoy_post_login_redirect");
                } catch {
                  /* storage disabled */
                }
                navigate("/");
              }}
              className="underline hover:text-white"
              data-testid="help-delete-abort"
            >
              Return home
            </button>{" "}
            — nothing has been deleted.
          </p>
        </div>
      </main>

      {/* Hidden Google button — same trick as Landing */}
      <div
        ref={renderRef}
        aria-hidden="true"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: "none",
          overflow: "hidden",
        }}
      />
    </div>
  );
}

function MailIcon({ size = 18 }) {
  return <Mail size={size} strokeWidth={2.2} className="text-black" />;
}

function SsoButton({ onClick, disabled, testId, Icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full h-[46px] rounded-full bg-white text-black font-bold text-[15px] flex items-center justify-center gap-2.5 hover:opacity-90 active:opacity-80 disabled:opacity-60 transition-opacity"
      data-testid={testId}
    >
      {disabled ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : Icon ? (
        <Icon size={18} />
      ) : null}
      <span>{label}</span>
    </button>
  );
}
